import type { MailProviderClient, NormalizedMessage, NormalizedAttachment } from "./types";
import { ProviderError } from "./types";

export class GmailClient implements MailProviderClient {
  provider = "gmail" as const;

  constructor(private accessToken: string) {}

  private async fetch<T>(url: string): Promise<T> {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    });
    if (res.status === 401 || res.status === 403) {
      throw new ProviderError(`Gmail auth error: ${res.status}`, "token_expired");
    }
    if (res.status === 429) {
      throw new ProviderError("Gmail rate limited", "rate_limited");
    }
    if (!res.ok) {
      throw new ProviderError(`Gmail API error: ${res.status}`, "unknown");
    }
    return res.json();
  }

  async getProfile(): Promise<{ emailAddress: string; historyId?: string }> {
    return this.fetch<{ emailAddress: string; historyId?: string }>(
      "https://www.googleapis.com/gmail/v1/users/me/profile"
    );
  }

  async listMessagesWithAttachments(
    query = "has:attachment",
    pageToken?: string
  ): Promise<{
    messages: NormalizedMessage[];
    attachments: NormalizedAttachment[];
    nextPageToken?: string;
  }> {
    const params = new URLSearchParams({ q: query, maxResults: "100" });
    if (pageToken) params.set("pageToken", pageToken);

    const listData = await this.fetch<{
      messages?: { id: string; threadId: string }[];
      nextPageToken?: string;
    }>(`https://www.googleapis.com/gmail/v1/users/me/messages?${params}`);

    const messages: NormalizedMessage[] = [];
    const attachments: NormalizedAttachment[] = [];

    if (!listData.messages) return { messages, attachments };

    for (const msgRef of listData.messages) {
      try {
        const msg = await this.fetch<{
          id: string;
          threadId: string;
          payload?: {
            headers?: { name: string; value: string }[];
            parts?: {
              filename?: string;
              mimeType?: string;
              body?: { attachmentId?: string; size?: number };
            }[];
          };
          snippet?: string;
          labelIds?: string[];
          internalDate?: string;
        }>(`https://www.googleapis.com/gmail/v1/users/me/messages/${msgRef.id}?format=full`);

        const result = this.normalizeMessage(msg);
        messages.push(result.message);
        attachments.push(...result.attachments);
      } catch (err) {
        if (err instanceof ProviderError) throw err;
        console.warn("Failed to fetch message", msgRef.id, err);
      }
    }

    return {
      messages,
      attachments,
      nextPageToken: listData.nextPageToken,
    };
  }

  async listHistory(
    startHistoryId: string,
    pageToken?: string
  ): Promise<{
    messages: NormalizedMessage[];
    attachments: NormalizedAttachment[];
    historyId?: string;
    nextPageToken?: string;
  }> {
    const params = new URLSearchParams({ startHistoryId });
    if (pageToken) params.set("pageToken", pageToken);

    const data = await this.fetch<{
      history?: {
        messagesAdded?: { message?: { id: string; threadId: string } }[];
      }[];
      nextPageToken?: string;
      historyId?: string;
    }>(`https://www.googleapis.com/gmail/v1/users/me/history?${params}`);

    const messages: NormalizedMessage[] = [];
    const attachments: NormalizedAttachment[] = [];

    for (const entry of data.history || []) {
      for (const added of entry.messagesAdded || []) {
        if (!added.message) continue;
        try {
          const msg = await this.fetch<{
            id: string;
            threadId: string;
            payload?: {
              headers?: { name: string; value: string }[];
              parts?: {
                filename?: string;
                mimeType?: string;
                body?: { attachmentId?: string; size?: number };
              }[];
            };
            snippet?: string;
            labelIds?: string[];
            internalDate?: string;
          }>(`https://www.googleapis.com/gmail/v1/users/me/messages/${added.message.id}?format=full`);

          const result = this.normalizeMessage(msg);
          messages.push(result.message);
          attachments.push(...result.attachments);
        } catch (err) {
          if (err instanceof ProviderError) throw err;
          console.warn("Failed to fetch history message", added.message.id, err);
        }
      }
    }

    return {
      messages,
      attachments,
      historyId: data.historyId,
      nextPageToken: data.nextPageToken,
    };
  }

  private normalizeMessage(msg: {
    id: string;
    threadId: string;
    payload?: {
      headers?: { name: string; value: string }[];
      parts?: {
        filename?: string;
        mimeType?: string;
        body?: { attachmentId?: string; size?: number };
      }[];
    };
    snippet?: string;
    labelIds?: string[];
    internalDate?: string;
  }): { message: NormalizedMessage; attachments: NormalizedAttachment[] } {
    const headers = msg.payload?.headers || [];
    const subject = headers.find((h) => h.name === "Subject")?.value || "(no subject)";
    const from = headers.find((h) => h.name === "From")?.value || "";
    const fromMatch = from.match(/(.*)<(.*)>/);
    const senderName = fromMatch ? fromMatch[1].trim() : from;
    const senderEmail = fromMatch ? fromMatch[2].trim() : from;

    const message: NormalizedMessage = {
      id: msg.id,
      threadId: msg.threadId,
      subject,
      snippet: msg.snippet || "",
      senderEmail,
      senderName,
      receivedAt: msg.internalDate ? new Date(parseInt(msg.internalDate)).toISOString() : new Date().toISOString(),
      labels: msg.labelIds || [],
    };

    const attachments: NormalizedAttachment[] = [];
    for (const part of msg.payload?.parts || []) {
      if (part.filename && part.body?.attachmentId) {
        attachments.push({
          messageId: msg.id,
          attachmentId: part.body.attachmentId,
          filename: part.filename,
          mimeType: part.mimeType || "application/octet-stream",
          size: part.body.size || 0,
        });
      }
    }

    return { message, attachments };
  }

  async fetchAttachment(messageId: string, attachmentId: string): Promise<Blob> {
    const data = await this.fetch<{
      data?: string;
    }>(
      `https://www.googleapis.com/gmail/v1/users/me/messages/${messageId}/attachments/${attachmentId}`
    );
    if (!data.data) throw new Error("No attachment data");
    const bytes = Uint8Array.from(atob(data.data.replace(/-/g, "+").replace(/_/g, "/")), (c) =>
      c.charCodeAt(0)
    );
    return new Blob([bytes]);
  }
}
